import { useState } from 'react';
import TareaFormModal from './TareaFormModal';
import ConfirmModal from './ConfirmModal';

export default function TareasView({ 
  tareas, 
  seeds, 
  caballonesData, 
  hortelanos,
  currentHortelano,
  onSaveTask, 
  onToggleComplete,
  onDeleteTask 
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const handleAddClick = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleSave = (taskData) => {
    onSaveTask(taskData);
    setIsModalOpen(false);
  };

  // Helper to find where a seed is planted
  const getPlantedCaballones = (semillaId, variedad) => {
    if (!semillaId) return [];
    const foundIn = [];
    Object.keys(caballonesData).forEach(cabIndex => {
      const items = caballonesData[cabIndex];
      const hasPlant = items.some(i => i.seed.id === semillaId && (!variedad || i.variety === variedad));
      if (hasPlant) {
        foundIn.push(parseInt(cabIndex) + 1);
      }
    });
    return foundIn;
  };

  const sortedTareas = [...tareas].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  
  const pendientes = sortedTareas.filter(t => !t.realizada);
  const completadas = sortedTareas.filter(t => t.realizada);

  const renderTask = (task) => {
    const seed = task.semilla_id ? seeds.find(s => s.id === task.semilla_id) : null;
    const plantedIn = getPlantedCaballones(task.semilla_id, task.variedad);
    const completedBy = task.completada_por ? hortelanos.find(h => h.id === task.completada_por) : null;
    const assignedTo = task.asignado_a ? hortelanos.find(h => h.id === task.asignado_a) : null;

    const dateObj = new Date(task.fecha);
    const taskDate = new Date(dateObj);
    taskDate.setHours(0,0,0,0);
    const today = new Date();
    today.setHours(0,0,0,0);
    
    const isToday = !task.realizada && taskDate.getTime() === today.getTime();
    const isOverdue = !task.realizada && taskDate.getTime() < today.getTime();
    const isOverdueOrToday = isToday || isOverdue;

    const dateStr = dateObj.toLocaleDateString();
    let dateLabel = dateStr;
    let dateColor = 'var(--text-secondary)';
    let dateWeight = 'normal';
    
    if (isToday) {
      dateLabel = `Hoy (${dateStr})`;
      dateColor = 'var(--accent-color)';
      dateWeight = 'bold';
    } else if (isOverdue) {
      dateLabel = `Vencida (${dateStr})`;
      dateColor = 'var(--danger-color)';
      dateWeight = 'bold';
    }

    return (
      <div 
        key={task.id} 
        className="seed-manager-item" 
        style={{ 
          opacity: task.realizada ? 0.6 : 1, 
          position: 'relative',
          borderLeft: isOverdueOrToday ? '4px solid var(--danger-color)' : '4px solid transparent',
          backgroundColor: isOverdueOrToday ? 'rgba(244, 67, 54, 0.04)' : 'var(--surface-color)',
          paddingLeft: isOverdueOrToday ? '12px' : '16px',
          transition: 'var(--transition)'
        }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <button 
            onClick={() => onToggleComplete(task, currentHortelano.id)}
            style={{ 
              background: 'transparent', border: '2px solid var(--text-secondary)', 
              borderRadius: '50%', width: '24px', height: '24px', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', marginTop: '4px',
              backgroundColor: task.realizada ? 'var(--primary-color)' : 'transparent',
              borderColor: task.realizada ? 'var(--primary-color)' : 'var(--text-secondary)',
              color: 'white'
            }}
          >
            {task.realizada ? '✓' : ''}
          </button>
          
          <div style={{ flex: 1 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px', textDecoration: task.realizada ? 'line-through' : 'none' }}>
                {task.descripcion}
              </h4>
              <div style={{ fontSize: '12px', color: dateColor, fontWeight: dateWeight, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>📅</span> {dateLabel}
              </div>
            </div>
            
            {seed && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>{seed.icon}</span> {seed.name} {task.variedad ? `(${task.variedad})` : ''}
                {plantedIn.length > 0 && (
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>
                    Caballón {plantedIn.join(', ')}
                  </span>
                )}
              </div>
            )}

            {assignedTo && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>👤</span> Asignada a: <strong>{assignedTo.nombre}</strong> {assignedTo.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'}
              </div>
            )}

            {task.realizada && completedBy && (
              <div style={{ fontSize: '12px', color: 'var(--primary-color)', marginTop: '6px' }}>
                Completada por {completedBy.nombre} {completedBy.sexo === 'M' ? '👨‍🌾' : '👩‍🌾'}
              </div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '12px' }}>
          <button 
            onClick={() => handleEditClick(task)} 
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)', 
              border: 'none', 
              color: 'var(--text-secondary)', 
              cursor: 'pointer', 
              fontSize: '16px',
              padding: '10px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'var(--transition)',
              minWidth: '38px',
              minHeight: '38px'
            }}
            title="Editar"
          >
            ✏️
          </button>
          <button 
            onClick={() => {
              setTaskToDelete(task);
              setConfirmDeleteOpen(true);
            }} 
            style={{ 
              background: 'rgba(244, 67, 54, 0.1)', 
              border: 'none', 
              color: 'var(--danger-color)', 
              cursor: 'pointer', 
              fontSize: '16px',
              padding: '10px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'var(--transition)',
              minWidth: '38px',
              minHeight: '38px'
            }}
            title="Eliminar"
          >
            🗑️
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="animate-fade" style={{ flex: 1, padding: '16px', paddingBottom: '80px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>Mis Tareas</h2>
        <button className="btn" onClick={handleAddClick} style={{ width: 'auto', padding: '8px 16px' }}>
          ➕ Añadir Tarea
        </button>
      </div>

      {tareas.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '40px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📋</span>
          <p>No hay tareas registradas.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {pendientes.length > 0 && (
            <div>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '12px', textTransform: 'uppercase' }}>Pendientes</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pendientes.map(renderTask)}
              </div>
            </div>
          )}

          {completadas.length > 0 && (
            <div>
              <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '12px', textTransform: 'uppercase' }}>Completadas</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {completadas.map(renderTask)}
              </div>
            </div>
          )}
        </div>
      )}

      <TareaFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        initialData={editingTask}
        seeds={seeds}
        hortelanos={hortelanos}
      />

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        title="Eliminar Tarea"
        message={taskToDelete ? `¿Seguro que quieres eliminar la tarea "${taskToDelete.descripcion}"? Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => {
          if (taskToDelete) {
            onDeleteTask(taskToDelete.id);
          }
          setConfirmDeleteOpen(false);
          setTaskToDelete(null);
        }}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setTaskToDelete(null);
        }}
      />
    </div>
  );
}
